import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Text } from 'components/Base/Typography';

export type ChartConfig = {
  type: 'bar' | 'line' | 'pie';
  title?: string;
  labels: string[];
  datasets: Array<{
    label?: string;
    data: number[];
  }>;
};

type ChartDisplayProps = {
  chartConfig: ChartConfig | null | undefined;
  height?: number;
};

const COLORS = [
  '#9b88ec',
  '#ff931d',
  '#00c0da',
  '#fa66ca',
  '#09b157',
  '#6b4fe0',
  '#00779f',
];

const isValidConfig = (c: ChartConfig | null | undefined): c is ChartConfig => {
  if (!c) return false;
  if (!['bar', 'line', 'pie'].includes(c.type)) return false;
  if (!Array.isArray(c.labels) || c.labels.length === 0) return false;
  if (!Array.isArray(c.datasets) || c.datasets.length === 0) return false;
  return c.datasets.every(
    d => Array.isArray(d.data) && d.data.length === c.labels.length,
  );
};

type ChartRow = Record<string, string | number>;

const renderChart = (
  type: ChartConfig['type'],
  rows: ChartRow[],
  datasets: ChartConfig['datasets'],
) => {
  if (type === 'bar') {
    return (
      <BarChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        {datasets.map((ds, i) => (
          <Bar
            key={ds.label ?? `series_${i}`}
            dataKey={ds.label ?? `series_${i}`}
            fill={COLORS[i % COLORS.length]}
          />
        ))}
      </BarChart>
    );
  }

  if (type === 'line') {
    return (
      <LineChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        {datasets.map((ds, i) => (
          <Line
            key={ds.label ?? `series_${i}`}
            type="monotone"
            dataKey={ds.label ?? `series_${i}`}
            stroke={COLORS[i % COLORS.length]}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    );
  }

  return (
    <PieChart>
      <Tooltip />
      <Legend />
      <Pie
        data={rows}
        dataKey={datasets[0].label ?? 'series_0'}
        nameKey="name"
        outerRadius="80%"
        label
      >
        {rows.map((row, idx) => (
          <Cell key={String(row.name)} fill={COLORS[idx % COLORS.length]} />
        ))}
      </Pie>
    </PieChart>
  );
};

export const ChartDisplay: React.FC<ChartDisplayProps> = ({
  chartConfig,
  height = 300,
}) => {
  if (!isValidConfig(chartConfig)) return null;

  const { type, title, labels, datasets } = chartConfig;

  const rows: ChartRow[] = labels.map((label, idx) => {
    const row: ChartRow = { name: label };
    datasets.forEach((ds, dsIdx) => {
      row[ds.label ?? `series_${dsIdx}`] = ds.data[idx];
    });
    return row;
  });

  return (
    <figure style={{ width: '100%', margin: 0 }}>
      {title && (
        <figcaption style={{ marginBottom: 8 }}>
          <Text style={{ fontWeight: 600 }}>{title}</Text>
        </figcaption>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart(type, rows, datasets)}
      </ResponsiveContainer>
    </figure>
  );
};
